import React, { useState, useEffect, useCallback } from 'react';
import TouchButton from '../ui/TouchButton';
import { TouchInput, TouchSelect, TouchFormGroup, TouchCheckbox } from '../ui/TouchForm';
import SafetyZoneFilter from './SafetyZoneFilter';
import TimeBasedFilter from './TimeBasedFilter';
import LocationTypeFilter from './LocationTypeFilter';
import RatingFilter from './RatingFilter';
import DistanceFilter from './DistanceFilter';
import FilterPresets from './FilterPresets';

const AdvancedFilters = ({ 
  onFiltersChange,
  initialFilters = {},
  userLocation = null,
  showLocationFilters = true,
  className = ''
}) => {
  const [filters, setFilters] = useState({
    // Safety filters
    safetyZones: [], // ['safe', 'moderate', 'caution']
    minSafetyRating: null,
    maxSafetyRating: null,
    
    // Time-based filters
    reviewTimeRange: 'all', // 'last_week', 'last_month', 'last_3_months', 'last_year', 'custom', 'all'
    customDateRange: { start: null, end: null },
    lastUpdated: null,
    
    // Location type filters
    locationTypes: [], // Array of selected types
    categories: [], // Array of selected categories
    neighborhoods: [], // Array of neighborhood names
    
    // Rating filters
    minUserRating: null,
    minReviewCount: null,
    hasPhotos: null,
    hasReviews: null,
    verifiedOnly: false,
    
    // Distance filters
    distanceRadius: null, // meters
    centerPoint: userLocation,
    
    // Additional filters
    accessibility: [],
    priceRange: [],
    openNow: false,
    
    ...initialFilters
  });

  const [activeFilterCount, setActiveFilterCount] = useState(0);
  const [showPresets, setShowPresets] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    safety: true,
    time: false,
    location: false,
    rating: false,
    distance: showLocationFilters,
    additional: false
  });

  // Calculate active filter count
  useEffect(() => {
    let count = 0;
    
    // Safety filters
    if (filters.safetyZones.length > 0) count++;
    if (filters.minSafetyRating !== null) count++;
    if (filters.maxSafetyRating !== null) count++;
    
    // Time filters
    if (filters.reviewTimeRange !== 'all') count++;
    if (filters.lastUpdated !== null) count++;
    
    // Location filters
    if (filters.locationTypes.length > 0) count++;
    if (filters.categories.length > 0) count++;
    if (filters.neighborhoods.length > 0) count++;
    
    // Rating filters
    if (filters.minUserRating !== null) count++;
    if (filters.minReviewCount !== null) count++;
    if (filters.hasPhotos !== null) count++;
    if (filters.verifiedOnly) count++;
    
    // Distance filters
    if (filters.distanceRadius !== null) count++;
    
    // Additional filters
    if (filters.accessibility.length > 0) count++;
    if (filters.priceRange.length > 0) count++;
    if (filters.openNow) count++;
    
    setActiveFilterCount(count);
  }, [filters]);

  // Notify parent of filter changes
  useEffect(() => {
    onFiltersChange && onFiltersChange(filters);
  }, [filters, onFiltersChange]);

  const updateFilter = useCallback((key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  }, []);

  const resetFilters = () => {
    const defaultFilters = {
      safetyZones: [],
      minSafetyRating: null,
      maxSafetyRating: null,
      reviewTimeRange: 'all',
      customDateRange: { start: null, end: null },
      lastUpdated: null,
      locationTypes: [],
      categories: [],
      neighborhoods: [],
      minUserRating: null,
      minReviewCount: null,
      hasPhotos: null,
      hasReviews: null,
      verifiedOnly: false,
      distanceRadius: null,
      centerPoint: userLocation,
      accessibility: [],
      priceRange: [],
      openNow: false
    };
    setFilters(defaultFilters);
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const applyPreset = (preset) => {
    setFilters({
      ...filters,
      ...preset.filters
    });
    setShowPresets(false);
  };

  return (
    <div className={`advanced-filters ${className}`}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '16px',
        borderBottom: '1px solid #e9ecef',
        backgroundColor: '#f8f9fa'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
            🎯 Advanced Filters
          </h3>
          {activeFilterCount > 0 && (
            <span style={{
              backgroundColor: '#2196F3',
              color: 'white',
              borderRadius: '12px',
              padding: '2px 8px',
              fontSize: '12px',
              fontWeight: '600'
            }}>
              {activeFilterCount} active
            </span>
          )}
        </div>

        <div style={{ display: 'flex', gap: '8px' }}>
          <TouchButton
            onClick={() => setShowPresets(!showPresets)}
            variant="outline"
            size="small"
          >
            📋 Presets
          </TouchButton>
          
          {activeFilterCount > 0 && (
            <TouchButton
              onClick={resetFilters}
              variant="ghost"
              size="small"
              style={{ color: '#666' }}
            >
              Clear All
            </TouchButton>
          )}
        </div>
      </div>

      {/* Filter Presets */}
      {showPresets && (
        <div style={{
          padding: '16px',
          backgroundColor: '#ffffff',
          borderBottom: '1px solid #e9ecef'
        }}>
          <FilterPresets
            onApplyPreset={applyPreset}
            currentFilters={filters}
            onClose={() => setShowPresets(false)}
          />
        </div>
      )}

      {/* Filter Sections */}
      <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        {/* Safety Zone Filters */}
        <FilterSection
          title="🛡️ Safety Zones"
          isExpanded={expandedSections.safety}
          onToggle={() => toggleSection('safety')}
          activeCount={
            filters.safetyZones.length + 
            (filters.minSafetyRating !== null ? 1 : 0) + 
            (filters.maxSafetyRating !== null ? 1 : 0)
          }
        >
          <SafetyZoneFilter
            selectedZones={filters.safetyZones}
            onZonesChange={(zones) => updateFilter('safetyZones', zones)}
            minRating={filters.minSafetyRating}
            maxRating={filters.maxSafetyRating}
            onRatingRangeChange={(min, max) => {
              updateFilter('minSafetyRating', min);
              updateFilter('maxSafetyRating', max);
            }}
          />
        </FilterSection>

        {/* Time-Based Filters */}
        <FilterSection
          title="📅 Time & Recency"
          isExpanded={expandedSections.time}
          onToggle={() => toggleSection('time')}
          activeCount={
            (filters.reviewTimeRange !== 'all' ? 1 : 0) +
            (filters.lastUpdated !== null ? 1 : 0)
          }
        >
          <TimeBasedFilter
            timeRange={filters.reviewTimeRange}
            onTimeRangeChange={(range) => updateFilter('reviewTimeRange', range)}
            customDateRange={filters.customDateRange}
            onCustomDateRangeChange={(range) => updateFilter('customDateRange', range)}
            lastUpdated={filters.lastUpdated}
            onLastUpdatedChange={(value) => updateFilter('lastUpdated', value)}
          />
        </FilterSection>

        {/* Location Type Filters */}
        <FilterSection
          title="📍 Location Types"
          isExpanded={expandedSections.location}
          onToggle={() => toggleSection('location')}
          activeCount={
            filters.locationTypes.length +
            filters.categories.length +
            filters.neighborhoods.length
          }
        >
          <LocationTypeFilter
            selectedTypes={filters.locationTypes}
            onTypesChange={(types) => updateFilter('locationTypes', types)}
            selectedCategories={filters.categories}
            onCategoriesChange={(categories) => updateFilter('categories', categories)}
            selectedNeighborhoods={filters.neighborhoods}
            onNeighborhoodsChange={(neighborhoods) => updateFilter('neighborhoods', neighborhoods)}
          />
        </FilterSection>

        {/* User Rating Filters */}
        <FilterSection
          title="⭐ Reviews & Ratings"
          isExpanded={expandedSections.rating}
          onToggle={() => toggleSection('rating')}
          activeCount={
            (filters.minUserRating !== null ? 1 : 0) +
            (filters.minReviewCount !== null ? 1 : 0) +
            (filters.hasPhotos !== null ? 1 : 0) +
            (filters.verifiedOnly ? 1 : 0)
          }
        >
          <RatingFilter
            minRating={filters.minUserRating}
            onMinRatingChange={(rating) => updateFilter('minUserRating', rating)}
            minReviewCount={filters.minReviewCount}
            onMinReviewCountChange={(count) => updateFilter('minReviewCount', count)}
            hasPhotos={filters.hasPhotos}
            onHasPhotosChange={(value) => updateFilter('hasPhotos', value)}
            hasReviews={filters.hasReviews}
            onHasReviewsChange={(value) => updateFilter('hasReviews', value)}
            verifiedOnly={filters.verifiedOnly}
            onVerifiedOnlyChange={(value) => updateFilter('verifiedOnly', value)}
          />
        </FilterSection>

        {/* Distance Filters */}
        {showLocationFilters && (
          <FilterSection
            title="📏 Distance & Location"
            isExpanded={expandedSections.distance}
            onToggle={() => toggleSection('distance')}
            activeCount={filters.distanceRadius !== null ? 1 : 0}
          >
            <DistanceFilter
              radius={filters.distanceRadius}
              onRadiusChange={(radius) => updateFilter('distanceRadius', radius)}
              centerPoint={filters.centerPoint}
              onCenterPointChange={(point) => updateFilter('centerPoint', point)}
              userLocation={userLocation}
            />
          </FilterSection>
        )}

        {/* Additional Filters */}
        <FilterSection
          title="➕ Additional Options"
          isExpanded={expandedSections.additional}
          onToggle={() => toggleSection('additional')}
          activeCount={
            filters.accessibility.length +
            filters.priceRange.length +
            (filters.openNow ? 1 : 0)
          }
        >
          <AdditionalFilters
            accessibility={filters.accessibility}
            onAccessibilityChange={(value) => updateFilter('accessibility', value)}
            priceRange={filters.priceRange}
            onPriceRangeChange={(value) => updateFilter('priceRange', value)}
            openNow={filters.openNow}
            onOpenNowChange={(value) => updateFilter('openNow', value)}
          />
        </FilterSection>
      </div>

      {/* Apply/Reset Actions */}
      <div style={{
        padding: '16px',
        borderTop: '1px solid #e9ecef',
        backgroundColor: '#f8f9fa',
        display: 'flex',
        gap: '12px'
      }}>
        <TouchButton
          onClick={() => onFiltersChange && onFiltersChange(filters)}
          style={{ flex: 1 }}
          disabled={activeFilterCount === 0}
        >
          Apply Filters ({activeFilterCount})
        </TouchButton>
        
        <TouchButton
          onClick={resetFilters}
          variant="outline"
          disabled={activeFilterCount === 0}
        >
          Reset
        </TouchButton>
      </div>
    </div>
  );
};

const FilterSection = ({ title, children, isExpanded, onToggle, activeCount }) => (
  <div style={{
    borderBottom: '1px solid #f0f0f0'
  }}>
    <div
      onClick={onToggle}
      style={{
        padding: '16px',
        cursor: 'pointer',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: isExpanded ? '#f8f9fa' : 'white',
        borderLeft: activeCount > 0 ? '3px solid #2196F3' : '3px solid transparent',
        transition: 'all 150ms ease'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span style={{ fontSize: '16px', fontWeight: '600' }}>
          {title}
        </span>
        {activeCount > 0 && (
          <span style={{
            backgroundColor: '#2196F3',
            color: 'white',
            borderRadius: '10px',
            padding: '2px 6px',
            fontSize: '11px',
            fontWeight: '600'
          }}>
            {activeCount}
          </span>
        )}
      </div>
      
      <span style={{
        fontSize: '14px',
        color: '#666',
        transition: 'transform 150ms ease',
        transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
      }}>
        ▼
      </span>
    </div>
    
    {isExpanded && (
      <div style={{
        padding: '0 16px 16px',
        backgroundColor: '#fafafa',
        animation: 'expandSection 200ms ease-out'
      }}>
        {children}
      </div>
    )}
  </div>
);

const AdditionalFilters = ({ 
  accessibility, 
  onAccessibilityChange, 
  priceRange, 
  onPriceRangeChange,
  openNow,
  onOpenNowChange 
}) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
    {/* Accessibility Options */}
    <TouchFormGroup>
      <label style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px', display: 'block' }}>
        ♿ Accessibility Features
      </label>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
        {[
          { value: 'wheelchair', label: '♿ Wheelchair Accessible' },
          { value: 'hearing-loop', label: '🔊 Hearing Loop' },
          { value: 'braille', label: '👆 Braille Signage' },
          { value: 'accessible-parking', label: '🅿️ Accessible Parking' }
        ].map(option => (
          <TouchCheckbox
            key={option.value}
            label={option.label}
            checked={accessibility.includes(option.value)}
            onChange={(e) => {
              const newAccessibility = e.target.checked
                ? [...accessibility, option.value]
                : accessibility.filter(v => v !== option.value);
              onAccessibilityChange(newAccessibility);
            }}
          />
        ))}
      </div>
    </TouchFormGroup>

    {/* Price Range */}
    <TouchFormGroup>
      <label style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px', display: 'block' }}>
        💰 Price Range
      </label>
      <div style={{ display: 'flex', gap: '8px' }}>
        {['$', '$$', '$$$', '$$$$'].map(price => (
          <TouchButton
            key={price}
            onClick={() => {
              const newPriceRange = priceRange.includes(price)
                ? priceRange.filter(p => p !== price)
                : [...priceRange, price];
              onPriceRangeChange(newPriceRange);
            }}
            variant={priceRange.includes(price) ? 'primary' : 'outline'}
            size="small"
            style={{ minWidth: '50px' }}
          >
            {price}
          </TouchButton>
        ))}
      </div>
    </TouchFormGroup>

    {/* Open Now */}
    <TouchFormGroup>
      <TouchCheckbox
        label="🕐 Open Now"
        checked={openNow}
        onChange={(e) => onOpenNowChange(e.target.checked)}
      />
    </TouchFormGroup>
  </div>
);

export default AdvancedFilters;

// Add CSS animations
const styles = `
  @keyframes expandSection {
    from {
      opacity: 0;
      max-height: 0;
    }
    to {
      opacity: 1;
      max-height: 500px;
    }
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement("style");
  styleSheet.type = "text/css";
  styleSheet.innerText = styles;
  document.head.appendChild(styleSheet);
}